import * as React from 'react';
import { Box, Typography, Card, Button, SvgIcon, MuiBox, color } from '@kis-technologies/kis-ui';
import { OPERATIONAL, NOT_CONNECTED, UNKNOWN, USER_ERROR } from 'constants/hardware';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { selectors as hardwareSelectors, actions as hardwareActions, Scanner as ScannerProps } from 'services/hardware';
import { ActionOverlay } from 'components/ActionOverlay';
import { useStyles } from '../styled';

export const BarcodeScanner = () => {
  // Actions and Selectors
  const { status, loading } = useSelector(hardwareSelectors.selectScanner) as ScannerProps;
  const { scannerPing, scannerRead } = useActions({
    scannerPing: hardwareActions.scannerPing,
    scannerRead: hardwareActions.scannerRead
  });

  React.useEffect(() => {
    if (status.code === UNKNOWN) scannerPing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show Troubleshoot Overlay
  const [showTroubleshoot, setShowTroubleshoot] = React.useState(false);
  const classes = useStyles();

  const onTroubleshootConfirm = () => {
    scannerPing(true);
    setShowTroubleshoot(false);
  };

  const scannerOperational = [OPERATIONAL, USER_ERROR].includes(status.code);

  // **************
  // **  RENDER  **
  // **************

  const renderMessage = () => {
    if (scannerOperational && loading) return 'Please scan a barcode. Results of the scan will be shown below.';
    if (scannerOperational) return 'Your barcode scanner is online and no additional action is required.';
    if (status.code === NOT_CONNECTED) return 'Your barcode scanner is experiencing some issues.';
    return null;
  };

  const renderAction = () => {
    if (scannerOperational) return <SvgIcon name="success_circle" size={46} color={color.GREEN} />;
    if (status.code === NOT_CONNECTED) return <SvgIcon name="error_circle" size={46} color={color.RED} />;
    return null;
  };

  const renderButton = () => {
    if (scannerOperational) {
      return (
        <Button customSize="medium" appearance="white" fullWidth onClick={scannerRead} disabled={loading}>
          Test Scan
        </Button>
      );
    }
    return (
      <Button customSize="medium" appearance="error" fullWidth onClick={() => setShowTroubleshoot(true)}>
        Resolve
      </Button>
    );
  };

  return (
    <>
      <MuiBox className={classes.systemCheckHardwareDevices} margin="0 3 0 0">
        <Card fullHeight hasBorderRadius raised>
          <Card.Title
            gutter={5}
            title={
              <Typography fontSize={24} fontWeight={900}>
                Barcode Scanner
              </Typography>
            }
            action={renderAction()}
          />
          <Card.Content gutter={5}>
            <Box flexOverride="1">
              <Typography fontSize={15}>{renderMessage()}</Typography>
              {status.message && (
                <Box margin="5 0">
                  <Typography fontSize={15} fontWeight={600}>
                    Scan data: {status.message}
                  </Typography>
                </Box>
              )}
            </Box>
            {renderButton()}
          </Card.Content>
        </Card>
      </MuiBox>
      {showTroubleshoot && (
        <ActionOverlay
          title="The system is experiencing an issue."
          description="Please contact your admin at this time for support. It is recommended you take the kiosk inactive while this issue is resolved."
          cancelText="Close"
          confirmText="Retest"
          onConfirm={onTroubleshootConfirm}
          onCancel={() => setShowTroubleshoot(false)}
        />
      )}
    </>
  );
};
